import { AxiosError } from 'axios';
import Vue from 'vue';
import store from '@/store';
import LoginService from '@/LoginService'
import ResetPasswordService from '@/ResetPasswordService';
import RegistrationService from '@/RegistrationService'
import ErrorHelper from '@/ErrorHelper'
import { trackGoogleAdsRegistration } from '@/GoogleAdsTracking';
import PasswordHelper from './PasswordHelper';
import RecaptchaClientService from './RecaptchaClientService';
import RecaptchaProtectedResponse from './RecaptchaProtectedResponse';

let recaptchaScriptPromise: Promise<void> | null = null;
let recaptchaScriptSiteKey: string | null = null;

function loadRecaptchaScript(recaptchaV3SiteKey: string): Promise<void> {
    if (window.grecaptcha) {
        return Promise.resolve()
    }

    if (!recaptchaScriptPromise || recaptchaScriptSiteKey !== recaptchaV3SiteKey) {
        recaptchaScriptSiteKey = recaptchaV3SiteKey
        recaptchaScriptPromise = new Promise((resolve, reject) => {
            const existingScript = document.getElementById('google-recaptcha-script') as HTMLScriptElement | null
            if (existingScript) {
                existingScript.remove()
            }

            const script = document.createElement('script')
            script.id = 'google-recaptcha-script'
            script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(recaptchaV3SiteKey)}`
            script.async = true
            script.defer = true
            script.onload = () => resolve()
            script.onerror = () => reject(new Error('Could not load reCAPTCHA'))
            document.head.appendChild(script)
        })
    }

    return recaptchaScriptPromise
}

function describeRecaptchaLoadError(error: unknown): string {
    if (error instanceof Error && error.message) {
        return error.message
    }

    const axiosError = error as AxiosError<{ message?: string }> | undefined
    const status = axiosError?.response?.status
    const responseMessage = axiosError?.response?.data?.message
    const requestUrl = axiosError?.config?.url

    if (status && requestUrl) {
        return `Config request failed (${status}) for ${requestUrl}${responseMessage ? `: ${responseMessage}` : ''}`
    }

    if (status) {
        return `Config request failed (${status})${responseMessage ? `: ${responseMessage}` : ''}`
    }

    return 'Unknown reCAPTCHA initialization error'
}

function describeRequestError(error: unknown): string {
    if (error instanceof Error && error.message) {
        const axiosError = error as AxiosError<{ message?: string }>
        const status = axiosError.response?.status
        const responseMessage = axiosError.response?.data?.message
        if (status) {
            return `${status}${responseMessage ? `: ${responseMessage}` : ''}`
        }
        return error.message
    }

    const axiosError = error as AxiosError<{ message?: string }> | undefined
    const status = axiosError?.response?.status
    const responseMessage = axiosError?.response?.data?.message
    if (status) {
        return `${status}${responseMessage ? `: ${responseMessage}` : ''}`
    }

    return 'Unknown request error'
}

export default Vue.component('loginform', {
    props: {
        initialOperation: Number,
        initialWantsToUpgrade: Boolean
    },
    data: () => ({
        email: "",
        password: "",
        confirmpassword: "",
        userError: "",
        operations: [ "Login", "Register", "Reset" ],
        operation: 0,
        loading: false,
        snackbar: false,
        snackbarText: '',
        snackbarColor: '',
        wantsToUpgrade: false,
        loginPostUrl: LoginService.loginPostUrl(),
        recaptchaReady: false,
        requireRecaptchaV2: false,
        recaptchaV2SiteKey: '',
        recaptchaV3SiteKey: '',
        recaptchaV2Token: null as string | null,
        recaptchaV3Token: null as string | null,
        recaptchaV2WidgetId: null as number | null
    }),
    mounted: async function () {
        // Keep the modal centered against the viewport, not Vuetify's app-content region.
        document.body.appendChild(this.$el)

        this.operation = this.initialOperation ?? 0
        this.wantsToUpgrade = this.initialWantsToUpgrade

        try {
            const response = await RecaptchaClientService.getConfig()
            this.recaptchaV2SiteKey = response.data.recaptchaV2SiteKey
            this.recaptchaV3SiteKey = response.data.recaptchaV3SiteKey
            if (!this.recaptchaV2SiteKey || !this.recaptchaV3SiteKey) {
                throw new Error(`Config missing site keys. v2=${!!this.recaptchaV2SiteKey} v3=${!!this.recaptchaV3SiteKey}`)
            }
            await loadRecaptchaScript(this.recaptchaV3SiteKey)
            this.recaptchaReady = true
        } catch (error) {
            console.error('reCAPTCHA initialization failed', error)
            this.showError(`Could not load reCAPTCHA: ${describeRecaptchaLoadError(error)}`)
        }
    },
    beforeDestroy: function () {
        if (this.$el.parentElement === document.body) {
            document.body.removeChild(this.$el)
        }
    },
    methods: {
        getInputValue: function(selector: string): string {
            const input = this.$el.querySelector(selector) as HTMLInputElement | null
            return input?.value ?? ''
        },
        readFormValue: function(...names: string[]): string {
            for (const name of names) {
                const value = this.getInputValue(`input[name="${name}"]`)
                if (value) {
                    return value
                }
            }

            return ''
        },
        readFieldsFromForm: function() {
            this.email = this.readFormValue('username', 'email')
            this.password = this.readFormValue('password')
            this.confirmpassword = this.readFormValue('confirmpassword')
        },
        appendHiddenInput: function(form: HTMLFormElement, name: string, value: string) {
            const input = document.createElement('input')
            input.type = 'hidden'
            input.name = name
            input.value = value
            form.appendChild(input)
        },
        submitGoogleLogin: function () {
            const form = document.createElement('form')
            form.action = this.loginPostUrl
            form.method = 'POST'
            form.enctype = 'application/x-www-form-urlencoded'
            this.appendHiddenInput(form, 'returnUrl', this.$router.currentRoute.fullPath)
            this.appendHiddenInput(form, 'loginType', 'google')
            document.body.appendChild(form)
            form.submit()
        },
        clearFields: function () {
            this.email = ""
            this.password = ""
            this.confirmpassword = ""
        },
        selectOperation: function (index: number) {
            this.operation = index
            this.clearFields()
            this.resetRecaptchaState()
        },
        showError: function (message: string) {
            this.snackbarText = message;
            this.snackbar = true
            this.snackbarColor = 'error'
        },
        showInfo: function (message: string) {
            this.snackbarText = message;
            this.snackbar = true
            this.snackbarColor = ''
        },
        canLogin: function() {
            return this.recaptchaReady
        },
        login: async function () {
            this.readFieldsFromForm()
            if (!this.email || !this.password) {
                this.showError('Please enter your email and password.')
                return
            }

            this.loading = true
            try {
                await this.populateRecaptchaToken('login')
                const resp = await LoginService.loginLocal({
                    username: this.email,
                    password: this.password,
                    requireRecaptchaV2: this.requireRecaptchaV2,
                    recaptchaV2Token: this.recaptchaV2Token,
                    recaptchaV3Token: this.recaptchaV3Token
                })
                if (resp.data.user) {
                    await store.commit('login', resp.data.user)
                }
                this.resetRecaptchaState()
                this.close(resp.data.message)
            }
            catch (error) {
                console.error('login failed', error)
                if (!this.handleRecaptchaError(error)) {
                    ErrorHelper.ifUserError(error, (userError) => this.showError(userError)) ||
                        this.showError(`An error occurred logging in: ${describeRequestError(error)}`)
                }
            }
            finally {
                this.loading = false
            }
        },
        canRegister: function() {
            return this.recaptchaReady
        },
        register: async function () {
            this.readFieldsFromForm()
            if(!this.email || !this.password || !this.confirmpassword) {
                this.showError('Please enter your email and password.')
                return
            }

            if(!PasswordHelper.isValid(this.password, this.confirmpassword, this.showError))  {
                return
            }

            try {
                this.loading = true
                await this.populateRecaptchaToken('register')
                const resp = await RegistrationService.register({
                    email: this.email,
                    password: this.password,
                    requireRecaptchaV2: this.requireRecaptchaV2,
                    recaptchaV2Token: this.recaptchaV2Token,
                    recaptchaV3Token: this.recaptchaV3Token
                })
                trackGoogleAdsRegistration()
                this.resetRecaptchaState()
                this.close(resp.data.message)
            }
            catch (error) {
                console.error('register failed', error)
                if (!this.handleRecaptchaError(error)) {
                    ErrorHelper.ifUserError(error, (userError) => this.showError(userError)) ||
                        this.showError(`An error occurred registering: ${describeRequestError(error)}`)
                }
            }
            finally {
                this.loading = false
            }
        },
        canReset: function() {
            return this.recaptchaReady
        },
        reset: async function () {
            this.readFieldsFromForm()
            if(!this.email) {
                this.showError('Please enter your email.')
                return
            }

            this.loading = true
            try {
                await this.populateRecaptchaToken('forgot_password')
                const resp = await ResetPasswordService.requestResetToken({
                    email: this.email,
                    requireRecaptchaV2: this.requireRecaptchaV2,
                    recaptchaV2Token: this.recaptchaV2Token,
                    recaptchaV3Token: this.recaptchaV3Token
                })
                this.resetRecaptchaState()
                this.close(resp.data.message)
            }
            catch (error) {
                console.error('reset failed', error)
                if (!this.handleRecaptchaError(error)) {
                    ErrorHelper.ifUserError(error, (userError) => this.showError(userError)) ||
                        this.showError(`An error occurred resetting the password: ${describeRequestError(error)}`)
                }
            }
            finally {
                this.loading = false
            }
        },
        close: function (message: string | null) {
            this.$emit('close', message);
        },
        enterPressed: async function() {
            if(this.operations[this.operation] === 'Login') {
                if(this.canLogin()) {
                    this.login()
                }
            }
            else if(this.operations[this.operation] === 'Register') {
                if(this.canRegister()) {
                    this.register()
                }
            }
            else if (this.operations[this.operation] === 'Reset') {
                if(this.canReset()) {
                    this.reset();
                }
            }
        },
        resetRecaptchaState: function() {
            this.requireRecaptchaV2 = false
            this.recaptchaV2Token = null
            this.recaptchaV3Token = null
            if (window.grecaptcha) {
                if (this.recaptchaV2WidgetId !== null) {
                    window.grecaptcha.reset(this.recaptchaV2WidgetId)
                }
            }
        },
        populateRecaptchaToken: async function(action: string) {
            if (this.requireRecaptchaV2) {
                if (!this.recaptchaV2Token) {
                    throw new Error('reCAPTCHA v2 token missing')
                }
                this.recaptchaV3Token = null
                return
            }

            this.recaptchaV2Token = null
            this.recaptchaV3Token = await this.executeRecaptchaV3(action)
        },
        executeRecaptchaV3: function(action: string): Promise<string> {
            return new Promise((resolve, reject) => {
                if (!window.grecaptcha || !this.recaptchaV3SiteKey) {
                    reject(new Error('reCAPTCHA is not ready'))
                    return
                }
                window.grecaptcha.ready(async () => {
                    try {
                        const token = await window.grecaptcha!.execute(this.recaptchaV3SiteKey, { action })
                        resolve(token)
                    } catch (error) {
                        reject(error)
                    }
                })
            })
        },
        ensureV2Widget: function() {
            if (!window.grecaptcha || !this.requireRecaptchaV2 || !this.recaptchaV2SiteKey) {
                return
            }

            const container = document.getElementById('recaptchaV2Container')
            if (!container) {
                return
            }

            if (this.recaptchaV2WidgetId === null) {
                this.recaptchaV2WidgetId = window.grecaptcha.render(container, {
                    sitekey: this.recaptchaV2SiteKey,
                    callback: (token: string) => {
                        this.recaptchaV2Token = token
                    },
                    'expired-callback': () => {
                        this.recaptchaV2Token = null
                    },
                    'error-callback': () => {
                        this.recaptchaV2Token = null
                    }
                })
            } else {
                window.grecaptcha.reset(this.recaptchaV2WidgetId)
            }
        },
        handleRecaptchaError: function(error: any): boolean {
            const response = error?.response?.data as RecaptchaProtectedResponse | undefined
            if (!response?.requireRecaptchaV2) {
                return false
            }

            this.requireRecaptchaV2 = true
            this.recaptchaV3Token = null
            this.recaptchaV2Token = null
            this.showInfo(response.message || 'Please complete the reCAPTCHA challenge and try again.')
            this.$nextTick(() => this.ensureV2Widget())
            return true
        }
    }
});
